
/**
 * BTKBespokeColour
 * Shared module for bespoke painted colour selection.
 * Manages the popup, sessionStorage persistence, and trigger label rendering.
 *
 * sessionStorage key: btk_kitchen_context
 * Adds/reads: { ..., bespokeColour: { type: 'fab'|'ral', value: 'Elephant\'s Breath' } | null }
 */
 
window.BTKBespokeColour = (function () {
 
  const CONTEXT_KEY = 'btk_kitchen_context';
  const POPUP_ID    = 'btk-bespoke-popup-overlay';
 
  // ─── SESSION STORAGE HELPERS ──────────────────────────────────────────────
 
  function getContext() {
    try { return JSON.parse(sessionStorage.getItem(CONTEXT_KEY) || '{}'); }
    catch (e) { return {}; }
  }
 
  function saveContext(updates) {
    const ctx = getContext();
    Object.assign(ctx, updates);
    try { sessionStorage.setItem(CONTEXT_KEY, JSON.stringify(ctx)); } catch (e) {}
  }
 
  function getBespokeColour() {
    return getContext().bespokeColour || null;
  }
 
  function setBespokeColour(bespokeColour) {
    // bespokeColour: { type: 'fab'|'ral', value: 'Elephant\'s Breath' } or null
    saveContext({ bespokeColour });
  }
 
  // ─── TRIGGER LABEL TEXT ───────────────────────────────────────────────────
 
  function getTriggerLabel() {
    const bc = getBespokeColour();
    if (!bc || !bc.value) return 'Choose Bespoke Colour';
    if (bc.type === 'fab') return 'F&B: ' + bc.value;
    if (bc.type === 'ral') return 'RAL: ' + bc.value;
    return 'Choose Bespoke Colour';
  }
 
  // Update all trigger labels on the current page
  function refreshAllTriggers() {
    const label = getTriggerLabel();
    const hasSelection = !!getBespokeColour();
    document.querySelectorAll('[data-btk-bespoke-trigger]').forEach(btn => {
      btn.textContent = label;
      btn.classList.toggle('btk-bespoke-trigger--selected', hasSelection);
    });
  }
 
  // ─── POPUP ────────────────────────────────────────────────────────────────
 
  function ensurePopup() {
    if (document.getElementById(POPUP_ID)) return;
 
    const overlay = document.createElement('div');
    overlay.id        = POPUP_ID;
    overlay.className = 'btk-bespoke-overlay';
    overlay.innerHTML = `
      <div class="btk-bespoke-modal" role="dialog" aria-modal="true" aria-labelledby="btk-bespoke-title">
        <button class="btk-bespoke-close" id="btk-bespoke-close" aria-label="Close">×</button>
        <h3 class="btk-bespoke-title" id="btk-bespoke-title">Bespoke Painted Colour</h3>
        <p class="btk-bespoke-subtitle">Select a colour range and enter your chosen colour name or reference.</p>
 
        <div class="btk-bespoke-type-row">
          <button class="btk-bespoke-type-btn btk-bespoke-type-btn--active" data-btk-type="fab">
            Farrow &amp; Ball
          </button>
          <button class="btk-bespoke-type-btn" data-btk-type="ral">
            RAL Colour
          </button>
        </div>
 
        <div class="btk-bespoke-input-wrap">
          <label class="btk-bespoke-label" id="btk-bespoke-input-label">
            <span id="btk-bespoke-placeholder-text">Farrow &amp; Ball colour name</span>
          </label>
          <input
            id="btk-bespoke-input"
            class="btk-bespoke-input"
            type="text"
            placeholder="e.g. Elephant's Breath"
            maxlength="120"
            autocomplete="off"
          >
        </div>
 
        <div class="btk-bespoke-actions">
          <button class="btk-bespoke-confirm btn btn--primary" id="btk-bespoke-confirm">Confirm</button>
          <button class="btk-bespoke-cancel btn btn--secondary" id="btk-bespoke-cancel">Cancel</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
 
    // Bind internal popup events
    overlay.addEventListener('click', e => {
      if (e.target === overlay) closePopup();
      if (e.target.id === 'btk-bespoke-close')   closePopup();
      if (e.target.id === 'btk-bespoke-cancel')  closePopup();
      if (e.target.id === 'btk-bespoke-confirm') confirmPopup();
 
      const typeBtn = e.target.closest('[data-btk-type]');
      if (typeBtn) {
        overlay.querySelectorAll('[data-btk-type]').forEach(b => b.classList.remove('btk-bespoke-type-btn--active'));
        typeBtn.classList.add('btk-bespoke-type-btn--active');
        const placeholders = { fab: "Farrow & Ball colour name", ral: "RAL colour number" };
        const labelEl = document.getElementById('btk-bespoke-placeholder-text');
        if (labelEl) labelEl.textContent = placeholders[typeBtn.dataset.btkType] || '';
        const input = document.getElementById('btk-bespoke-input');
        if (input) input.placeholder = typeBtn.dataset.btkType === 'fab' ? "e.g. Elephant's Breath" : "e.g. 7016";
      }
    });
 
    overlay.addEventListener('keydown', e => {
      if (e.key === 'Escape') closePopup();
      if (e.key === 'Enter') {
        const input = document.getElementById('btk-bespoke-input');
        if (document.activeElement === input) confirmPopup();
      }
    });
  }
 
  function openPopup() {
    ensurePopup();
    const overlay = document.getElementById(POPUP_ID);
    if (!overlay) return;
 
    // Pre-populate from current selection
    const bc = getBespokeColour();
    const typeToActivate = (bc && bc.type) ? bc.type : 'fab';
 
    overlay.querySelectorAll('[data-btk-type]').forEach(b => {
      b.classList.toggle('btk-bespoke-type-btn--active', b.dataset.btkType === typeToActivate);
    });
 
    const labelEl = document.getElementById('btk-bespoke-placeholder-text');
    if (labelEl) {
      labelEl.textContent = typeToActivate === 'fab' ? 'Farrow & Ball colour name' : 'RAL colour number';
    }
 
    const input = document.getElementById('btk-bespoke-input');
    if (input) {
      input.placeholder = typeToActivate === 'fab' ? "e.g. Elephant's Breath" : "e.g. 7016";
      input.value = (bc && bc.value) ? bc.value : '';
      setTimeout(() => { input.focus(); input.select(); }, 60);
    }
 
    overlay.classList.add('btk-bespoke-overlay--open');
  }
 
  function closePopup() {
    const overlay = document.getElementById(POPUP_ID);
    if (overlay) overlay.classList.remove('btk-bespoke-overlay--open');
  }
 
  function confirmPopup() {
    const overlay = document.getElementById(POPUP_ID);
    if (!overlay) return;
 
    const activeTypeBtn = overlay.querySelector('.btk-bespoke-type-btn--active');
    const type  = activeTypeBtn ? activeTypeBtn.dataset.btkType : 'fab';
    const input = document.getElementById('btk-bespoke-input');
    const value = input ? input.value.trim() : '';
 
    // Allow confirming even if empty (customer may just be browsing)
    const bc = value ? { type, value } : null;
    setBespokeColour(bc);
 
    closePopup();
    refreshAllTriggers();
  }
 
  // ─── GLOBAL CLICK DELEGATE ────────────────────────────────────────────────
  // Listens for clicks on any [data-btk-bespoke-trigger] button anywhere on the page.
 
  function bindTriggers() {
    document.addEventListener('click', e => {
      const trigger = e.target.closest('[data-btk-bespoke-trigger]');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        openPopup();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindTriggers);
  } else {
    bindTriggers();
  }
 
  // ─── INIT ─────────────────────────────────────────────────────────────────
 
  function init() {
    refreshAllTriggers();
  }
 
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
 
  // ─── PUBLIC API ───────────────────────────────────────────────────────────
 
  return {
    openPopup,
    closePopup,
    getBespokeColour,
    setBespokeColour,
    getTriggerLabel,
    refreshAllTriggers,
  };
 
})();